import { useEffect, useRef } from 'react'

import { trackEvent } from '~/shared/lib/analytics'

import { DOCUMENT_USER_ACTIVE, DOCUMENT_USER_INACTIVE } from '../config'
import { useStepSession } from './step-session-context'

const INACTIVITY_TIMEOUT_MS = 30_000

export function useInactivityDetection() {
  const { step, documentKey, hasDownloaded, addIdleTime } = useStepSession()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inactiveAtRef = useRef<number | null>(null)

  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(() => {
        inactiveAtRef.current = Date.now()
        trackEvent(DOCUMENT_USER_INACTIVE, {
          step,
          document_key: documentKey,
          has_downloaded: hasDownloaded,
        })
      }, INACTIVITY_TIMEOUT_MS)
    }

    const handleActivity = () => {
      const inactiveAt = inactiveAtRef.current
      if (inactiveAt !== null) {
        const durationSec = (Date.now() - inactiveAt) / 1000
        trackEvent(DOCUMENT_USER_ACTIVE, {
          step,
          document_key: documentKey,
          has_downloaded: hasDownloaded,
          inactive_duration_sec: Math.round(durationSec * 100) / 100,
        })
        addIdleTime(durationSec)
        inactiveAtRef.current = null
      }
      resetTimer()
    }

    const events = ['mousemove', 'scroll', 'keydown'] as const
    const options: AddEventListenerOptions = { passive: true }

    events.forEach((e) => document.addEventListener(e, handleActivity, options))
    resetTimer()

    return () => {
      events.forEach((e) => document.removeEventListener(e, handleActivity))
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [step, documentKey, hasDownloaded, addIdleTime])
}
